import {
  Input,
  Dropdown,
  Option,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular, DismissRegular } from '@fluentui/react-icons';
import i18n from '../../i18n';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: '200px',
    maxWidth: '300px',
  },
  filterDropdown: {
    minWidth: '140px',
  },
  clearButton: {
    marginLeft: 'auto',
  },
  filterCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginLeft: '4px',
  },
});

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  placeholder?: string;
  options: FilterOption[];
}

interface TableToolbarProps {
  // Search
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;

  // Filters
  filters?: FilterConfig[];
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;

  // Clear
  onClearAll?: () => void;
  showClearButton?: boolean;

  // Result count
  resultCount?: number;
  totalCount?: number;
}

export function TableToolbar({
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  filters = [],
  activeFilters,
  onFilterChange,
  onClearAll,
  showClearButton = true,
  resultCount,
  totalCount,
}: TableToolbarProps) {
  const styles = useStyles();

  const hasActiveFilters = Object.keys(activeFilters).length > 0 || searchValue.length > 0;

  return (
    <div className={styles.toolbar}>
      <Input
        className={styles.searchInput}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(_, data) => onSearchChange(data.value)}
        contentBefore={<SearchRegular />}
        contentAfter={
          searchValue ? (
            <Button
              appearance="transparent"
              icon={<DismissRegular />}
              size="small"
              onClick={() => onSearchChange('')}
              aria-label={i18n.t('toolbar.clearSearch')}
            />
          ) : undefined
        }
      />

      {filters.map(filter => (
        <Dropdown
          key={filter.key}
          className={styles.filterDropdown}
          placeholder={filter.placeholder || filter.label}
          value={
            activeFilters[filter.key]
              ? filter.options.find(o => o.value === activeFilters[filter.key])?.label
              : undefined
          }
          onOptionSelect={(_, data) => onFilterChange(filter.key, data.optionValue as string)}
        >
          <Option value="all" text={i18n.t('toolbar.all', { label: filter.label })}>{i18n.t('toolbar.all', { label: filter.label })}</Option>
          {filter.options.map(option => (
            <Option key={option.value} value={option.value} text={option.label}>
              {option.label}
            </Option>
          ))}
        </Dropdown>
      ))}

      {showClearButton && hasActiveFilters && onClearAll && (
        <Button
          className={styles.clearButton}
          appearance="subtle"
          icon={<DismissRegular />}
          onClick={onClearAll}
        >
          {i18n.t('toolbar.clearAll')}
        </Button>
      )}

      {resultCount !== undefined && totalCount !== undefined && resultCount !== totalCount && (
        <span className={styles.filterCount}>
          {i18n.t('toolbar.showing', { count: resultCount, total: totalCount })}
        </span>
      )}
    </div>
  );
}
